import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { queryCppCollegeTeamsAPI } from '../../../../actions/college-corner.actions';
import Table from '../../../../components/Table';
import { useStatsig } from '../../../../components/hooks/useStatsig';
import { parseQueryParams, setQueryParams } from '../../../../utils/collegeCorner';
import CollegeCornerFilters from '../../filters/CollegeCornerFilters';
import config from '../../../../../config';
import { initialState } from '../helpers';
import styles from './CppCollegeTeams.scss';
import { getCppColumns, getCppInitialFilters } from './cppHelpers';

function CppCollegeTeams({ history, location, viewerGender }) {
    const dispatch = useDispatch();
    const { checkGate, featureGates } = useStatsig();
    const [data, setData] = useState(initialState);
    const [filters, setFilters] = useState(null);
    const [tempFilters, setTempFilters] = useState({});
    const callCollegeTeamsAPI = (params) => dispatch(queryCppCollegeTeamsAPI(params));

    const clubRedirectBaseUrl = checkGate(featureGates.CLUB_LINKS_REDIRECT)
        ? `${config.nextAppHost}/college`
        : null;
    const columns = useMemo(
        () => getCppColumns({ viewerGender, clubRedirectBaseUrl }),
        [viewerGender, clubRedirectBaseUrl],
    );
    const initialFilters = useMemo(() => getCppInitialFilters(viewerGender), [viewerGender]);

    useEffect(() => {
        setFilters({ ...initialFilters, ...parseQueryParams(location.search) });
    }, [initialFilters]);

    useEffect(() => {
        if (filters) {
            const query = setQueryParams({ ...filters, gender: undefined });
            history.replace(`${location.pathname}?${query}`);
        }
    }, [filters]);

    const onAPILoad = async (apiData, reset = false) => {
        if (reset) {
            setData(initialState);
        }

        const response = await callCollegeTeamsAPI(apiData);
        const { rows, count } = response || {};
        if (rows) {
            setData(() => ({
                rows,
                count,
                isLoad: false,
            }));
        } else {
            setData((prevState) => ({
                ...prevState,
                isLoad: false,
            }));
        }
    };

    const onhandleSort = (sortValue) => {
        if (sortValue && sortValue === filters.sort) {
            setFilters((prevState) => ({
                ...prevState,
                order: filters.order === 'asc' ? 'desc' : 'asc',
                page: 1,
            }));
        } else {
            setFilters((prevState) => ({
                ...prevState,
                sort: sortValue,
                order: 'desc',
                page: 1,
            }));
        }
    };

    const onLoadMore = () => {
        setFilters((prevState) => ({
            ...prevState,
            page: prevState.page + 1,
        }));
    };

    const isPropsValid = (props) => {
        return props
            ? Object.keys(props).filter((v) => Object.keys(filters).indexOf(v) > -1)
                .length > 0
            : false;
    };

    const onhandleUpdateFilters = (updatedFilters) => {
        const updatedProps = isPropsValid(updatedFilters) ? updatedFilters : {};
        setTempFilters((prevState) => ({
            ...prevState,
            ...updatedProps,
        }));
    };

    const onhandleFilterSubmit = (props = {}) => {
        const updatedProps = isPropsValid(props) ? props : {};
        setFilters((prevState) => ({
            ...prevState,
            ...tempFilters,
            ...updatedProps,
            page: 1,
        }));
        setTempFilters({});
    };

    const onhandleResetFilters = () => {
        setTempFilters({});
        setFilters({ ...initialFilters });
    };

    return (
        <div className={`${styles.CppCollegeTeams}`}>
            <CollegeCornerFilters
                tempFilters={tempFilters}
                defaultFilters={filters}
                onhandleUpdateFilters={onhandleUpdateFilters}
                onhandleFilterSubmit={onhandleFilterSubmit}
                onhandleResetFilters={onhandleResetFilters}
                filterOptions={['gender', 'conference', 'division', 'location', 'utrFit']}
                sortingOptions={columns
                    .filter((v) => v.sortable !== false)
                    .map((v) => ({ label: v.sortLabel, value: v.key }))}
            />
            <Table
                data-testid="college-corner.cpp-college-teams.table"
                sortRuleChanger={onhandleSort}
                columns={columns}
                data={data}
                filters={filters}
                asyncCall={onAPILoad}
                onhandleClickLoadMore={onLoadMore}
                customNoRowsText="There are no results that meet this criteria."
                mergeData
            />
        </div>
    );
}

CppCollegeTeams.propTypes = {
    history: PropTypes.instanceOf(Object).isRequired,
    location: PropTypes.instanceOf(Object).isRequired,
    viewerGender: PropTypes.string,
};

CppCollegeTeams.defaultProps = {
    viewerGender: null,
};

export default CppCollegeTeams;
